import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewConfigFilterNode,
} from '@salescore/core'
import { HashMap, Option, Predicate } from 'effect'
import { useCallback } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'

import type { ViewConfigFilterNodeForForm } from '../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/filter/common'
import { calculateRowHeight, useRowLinesCount } from '../../hooks/useRowLinesCount'
import { type RowConfig, rowConfigsImplAtom } from '../rowConfig'
import { drillDownModalAtom, type DrillDownParameter } from './atoms'

export const useNavigationModal = () => {
  const initialState = { hasChanges: false }
  const [modal, setModal] = useRecoilState(drillDownModalAtom)
  const showModal = (content: DrillDownParameter) => {
    setModal((x) => ({
      visible: true,
      content,
      innerState: initialState,
    }))
  }
  const hideModal = () => {
    setModal((x) => ({
      ...x,
      visible: false,
    }))
  }
  const setInnerState = (innerState: { hasChanges: boolean }) => {
    setModal((x) => ({
      ...x,
      innerState,
    }))
  }

  return {
    drillDownModal: {
      isModalVisible: modal.visible,
      content: modal.content,
      modal,
      innerState: modal.innerState,
      showModal,
      hideModal,
      setInnerState,
    },
  }
}

export const useCalculatedRowHeight = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
): number => {
  const [rowLinesCount] = useRowLinesCount()

  return calculateRowHeight(compiledConfig.ui[0], rowLinesCount)
}

export const useSetRowConfig = (): ((id: string, updater: (oldValue: RowConfig) => RowConfig) => void) => {
  const setRowConfigsImpl = useSetRecoilState(rowConfigsImplAtom)
  return useCallback(
    (id: string, updater: (oldValue: RowConfig) => RowConfig) => {
      setRowConfigsImpl(
        HashMap.modifyAt(id, (config) =>
          config.pipe(
            Option.map(updater),
            Option.orElse(() => Option.some(updater({ expanded: false }))),
          ),
        ),
      )
    },
    [setRowConfigsImpl],
  )
}

export function convertToFilterNodeForForm(filterTree: ViewConfigFilterNode): ViewConfigFilterNodeForForm {
  return {
    ...filterTree,
    leafs: filterTree.leafs.map((x) => (x.type === 'property' ? x : undefined)).filter(Predicate.isNotNullish),
    children: filterTree.children.map((child) => convertToFilterNodeForForm(child)),
  }
}
